import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, FileText, Shield, type LucideIcon } from 'lucide-react';
import { Tabs } from '../../../../components/ui/index.js';
import { Panel, PanelHeader } from './PanelHeader.js';

export type OutputKind = 'artifact' | 'task' | 'decision';

export interface OutputRow {
  id: string;
  kind: OutputKind;
  name: string;
  sub: string;
  when: string;
  href?: string | null;
}

interface OutputsPanelProps {
  outputs?: OutputRow[];
  total?: number;
  openAllHref?: string | null;
}

const KIND_META: Record<OutputKind, { icon: LucideIcon; tint: string }> = {
  artifact: { icon: FileText, tint: 'bg-accent-muted text-accent-text' },
  task: { icon: CheckCircle2, tint: 'bg-success-muted text-success' },
  decision: { icon: Shield, tint: 'bg-warning-muted text-warning' },
};

const TABS: Array<{ key: 'all' | OutputKind; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'artifact', label: 'Artifacts' },
  { key: 'task', label: 'Tasks' },
  { key: 'decision', label: 'Decisions' },
];

export const OutputsPanel: React.FC<OutputsPanelProps> = ({ outputs = [], total, openAllHref }) => {
  const [tab, setTab] = useState<'all' | OutputKind>('all');
  const filtered = tab === 'all' ? outputs : outputs.filter((o) => o.kind === tab);
  const counts = {
    all: outputs.length,
    artifact: outputs.filter((o) => o.kind === 'artifact').length,
    task: outputs.filter((o) => o.kind === 'task').length,
    decision: outputs.filter((o) => o.kind === 'decision').length,
  };

  return (
    <Panel>
      <PanelHeader
        title="Outputs"
        subtitle="Artifacts, tasks, and decisions in linked project threads"
        right={
          openAllHref && outputs.length > 0 ? (
            <Link to={openAllHref} className="text-xs text-accent-text hover:underline">
              Open all {total ?? outputs.length} →
            </Link>
          ) : (
            <span className="text-xs text-faint" aria-disabled="true">
              Open all {total ?? outputs.length} →
            </span>
          )
        }
      />
      <div className="border-b border-border px-4 py-2">
        <Tabs
          variant="segmented"
          aria-label="Output kind"
          tabs={TABS.map((t) => ({ id: t.key, label: t.label, count: counts[t.key] }))}
          activeTab={tab}
          onChange={(id) => setTab(id as 'all' | OutputKind)}
        />
      </div>
      {filtered.length === 0 ? (
        <div className="px-4 py-6 text-xs text-muted">No outputs in this category yet.</div>
      ) : (
        <ul className="divide-y divide-border">
          {filtered.map((o) => {
            const meta = KIND_META[o.kind];
            const KindIcon = meta.icon;
            const content = (
              <>
                <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${meta.tint}`}>
                  <KindIcon size={13} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-heading">{o.name}</div>
                  <div className="truncate text-xs text-muted">{o.sub}</div>
                </div>
                <span className="font-mono text-[11px] text-faint">{o.when}</span>
                {o.href && <ArrowRight size={12} className="text-faint opacity-0 transition group-hover:opacity-100" />}
              </>
            );
            return (
              <li key={o.id}>
                {o.href ? (
                  <Link to={o.href} className="group flex items-center gap-3 px-4 py-2.5 hover:bg-surface-hover">
                    {content}
                  </Link>
                ) : (
                  <div className="flex items-center gap-3 px-4 py-2.5">{content}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
};
